import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';

export interface Column<T> {
  header: string;
  accessor: keyof T | ((row: T) => React.ReactNode);
  className?: string;
  /** Hide this column on mobile (< sm breakpoint) */
  mobileHidden?: boolean;
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  onRowClick?: (row: T) => void;
  pagination?: {
    currentPage: number;
    totalPages: number;
    total?: number;
    onPageChange: (page: number) => void;
  };
  emptyMessage?: string;
  showViewButton?: boolean;
  selectable?: boolean;
  selectedRows?: (string | number)[];
  onSelectionChange?: (selectedIds: (string | number)[]) => void;
  actions?: (row: T) => React.ReactNode;
}

export function DataTable<T extends { id: string | number }>({
  data,
  columns,
  onRowClick,
  pagination,
  emptyMessage = 'لا توجد بيانات',
  showViewButton = false,
  selectable = false,
  selectedRows = [],
  onSelectionChange,
  actions,
}: DataTableProps<T>) {
  const { isRTL } = useI18n();

  const allSelected = data.length > 0 && data.every((row) => selectedRows.includes(row.id));
  const someSelected = data.some((row) => selectedRows.includes(row.id)) && !allSelected;

  const handleSelectAll = () => {
    if (allSelected) {
      onSelectionChange?.([]);
    } else {
      onSelectionChange?.(data.map((row) => row.id));
    }
  };

  const handleSelectRow = (id: string | number) => {
    if (selectedRows.includes(id)) {
      onSelectionChange?.(selectedRows.filter((rowId) => rowId !== id));
    } else {
      onSelectionChange?.([...selectedRows, id]);
    }
  };

  const hasActions = actions || (showViewButton && onRowClick);

  const getPageNumbers = () => {
    if (!pagination) return [];
    const { currentPage, totalPages } = pagination;
    const pages: (number | string)[] = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }

    return pages;
  };

  // Direction-aware pagination icons:
  // In RTL: "previous" = move right (ChevronRight), "next" = move left (ChevronLeft)
  // In LTR: "previous" = move left (ChevronLeft), "next" = move right (ChevronRight)
  const PrevIcon = isRTL ? ChevronRight : ChevronLeft;
  const NextIcon = isRTL ? ChevronLeft : ChevronRight;

  return (
    <div className="space-y-4">
      <div className="rounded-lg border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="h-10">
              {selectable && (
                <TableHead className="w-7 text-center py-2">
                  <Checkbox
                    checked={allSelected}
                    ref={(el) => {
                      if (el) (el as any).indeterminate = someSelected;
                    }}
                    onCheckedChange={handleSelectAll}
                    aria-label="تحديد الكل"
                    className="!h-3 !w-3"
                  />
                </TableHead>
              )}
              {columns.map((column, index) => (
                <TableHead
                  key={index}
                  className={`text-start py-2 text-xs ${column.mobileHidden ? 'hidden sm:table-cell' : ''} ${column.className || ''}`}
                >
                  {column.header}
                </TableHead>
              ))}
              {hasActions && (
                <TableHead className="text-start py-2 text-xs">إجراءات</TableHead>
              )}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length + (selectable ? 1 : 0) + (hasActions ? 1 : 0)}
                  className="h-24 text-center"
                >
                  {emptyMessage}
                </TableCell>
              </TableRow>
            ) : (
              data.map((row) => (
                <TableRow
                  key={row.id}
                  onClick={!showViewButton && !actions ? () => onRowClick?.(row) : undefined}
                  className={`h-10 ${!showViewButton && !actions && onRowClick ? 'cursor-pointer hover:bg-muted/50' : ''}`}
                  data-state={selectedRows.includes(row.id) ? 'selected' : undefined}
                >
                  {selectable && (
                    <TableCell className="w-7 text-center py-1.5">
                      <Checkbox
                        checked={selectedRows.includes(row.id)}
                        onCheckedChange={() => handleSelectRow(row.id)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`تحديد الصف ${row.id}`}
                        className="!h-3 !w-3"
                      />
                    </TableCell>
                  )}
                  {columns.map((column, index) => (
                    <TableCell
                      key={index}
                      className={`text-start py-1.5 text-sm ${column.mobileHidden ? 'hidden sm:table-cell' : ''} ${column.className || ''}`}
                    >
                      {typeof column.accessor === 'function'
                        ? column.accessor(row)
                        : String(row[column.accessor])}
                    </TableCell>
                  ))}
                  {hasActions && (
                    <TableCell className="text-start py-1.5">
                      <div className="flex items-center justify-end gap-1">
                        {actions?.(row)}
                        {showViewButton && onRowClick && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs"
                            onClick={(e) => {
                              e.stopPropagation();
                              onRowClick(row);
                            }}
                          >
                            عرض
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {pagination && pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            {pagination.total !== undefined
              ? `عرض ${data.length} من ${pagination.total} نتيجة`
              : `صفحة ${pagination.currentPage} من ${pagination.totalPages}`}
          </p>
          <div className="flex items-center gap-1">
            <Button
              variant="outline"
              size="sm"
              className="h-8 px-2"
              onClick={() => pagination.onPageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
            >
              <PrevIcon className="h-4 w-4" />
            </Button>

            {getPageNumbers().map((page, index) =>
              typeof page === 'number' ? (
                <Button
                  key={index}
                  variant={pagination.currentPage === page ? 'default' : 'outline'}
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => pagination.onPageChange(page)}
                >
                  {page}
                </Button>
              ) : (
                <span key={index} className="px-1 text-muted-foreground">...</span>
              )
            )}

            <Button
              variant="outline"
              size="sm"
              className="h-8 px-2"
              onClick={() => pagination.onPageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === pagination.totalPages}
            >
              <NextIcon className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
